import { findElement, uniqueRef, useBuilder } from '../lib/builderStore';
import { typeMeta, type Choice, type FormElement, type PiiCategory } from '../lib/types';
import { Button, Checkbox, Field, IconButton, Input, Select, Textarea } from '../components/ui';
import { SortableRow, SortableRows } from './SortableRows';

const PII_OPTIONS: (PiiCategory | '')[] =
  ['', 'name', 'email', 'phone', 'address', 'national_id', 'health', 'financial', 'biometric'];

export function Properties() {
  const definition = useBuilder((s) => s.definition);
  const selectedRef = useBuilder((s) => s.selectedRef);
  const apply = useBuilder((s) => s.apply);

  const found = findElement(definition, selectedRef);

  if (!found) {
    return (
      <div className="p-4 text-sm text-[var(--ff-text-muted)]">
        Select a question to edit its settings.
      </div>
    );
  }

  const { element } = found;
  const meta = typeMeta(element.type);

  /** Mutates the selected element in place through the command stack. */
  const edit = (label: string, mutate: (el: FormElement) => void) =>
    apply(label, (draft) => {
      for (const page of draft.pages) {
        const target = page.elements.find((e) => e.ref === element.ref);
        if (target) { mutate(target); return; }

        // Questions inside a group are edited the same way. Searching only the top level
        // meant every edit to a group's child was silently dropped.
        for (const parent of page.elements) {
          const child = parent.elements?.find((c) => c.ref === element.ref);
          if (child) { mutate(child); return; }
        }
      }
    });

  if (element.kind === 'group') return <GroupProperties element={element} edit={edit} />;

  return (
    <div className="flex h-full flex-col">
      <header className="border-b border-[var(--ff-border)] px-4 py-3">
        <h2 className="text-sm font-semibold">{meta?.label ?? element.type}</h2>
        <p className="text-xs text-[var(--ff-text-muted)]">Question settings</p>
      </header>

      <div className="min-h-0 flex-1 space-y-4 overflow-auto p-4">
        <Field label="Question" required>
          <Textarea
            value={element.title ?? ''}
            onChange={(e) => edit('Edit title', (el) => { el.title = e.target.value; })}
            rows={2}
          />
        </Field>

        <Field label="Description" hint="Optional help text shown under the question.">
          <Input
            value={element.description ?? ''}
            onChange={(e) => edit('Edit description', (el) => {
              el.description = e.target.value || null;
            })}
          />
        </Field>

        <RefEditor element={element} />

        <div className="space-y-1 rounded-md border border-[var(--ff-border)] p-3">
          <Checkbox
            label="Required"
            checked={element.required ?? false}
            onChange={(v) => edit('Toggle required', (el) => { el.required = v; })}
          />
          <Checkbox
            label="Allow prefill"
            description="Off by default: an embed cannot pre-answer a question you have not opened up."
            checked={element.prefillable ?? false}
            onChange={(v) => edit('Toggle prefill', (el) => { el.prefillable = v; })}
          />
          <Checkbox
            label="Read only"
            description="Shown but not editable. Usually paired with prefill. This stops an
                         accidental edit, not a crafted request — prefill is the real gate."
            checked={element.readOnly ?? false}
            onChange={(v) => edit('Toggle read only', (el) => { el.readOnly = v; })}
          />
        </div>

        {(element.type === 'single_select' || element.type === 'multi_select'
          || element.type === 'likert') && (
          <Field label="Show choices as">
            <Select
              value={element.displayAs ?? ''}
              onChange={(e) => edit('Change choice display', (el) => {
                el.displayAs = e.target.value || null;
              })}
            >
              <option value="">List</option>
              <option value="buttons">Buttons</option>
            </Select>
          </Field>
        )}

        {meta?.piiCapable && (
          <Field
            label="Personal data"
            hint="Drives encryption, masked display, and exclusion from AI prompts."
          >
            <Select
              value={element.pii ?? ''}
              onChange={(e) => edit('Set PII category', (el) => {
                el.pii = (e.target.value || null) as PiiCategory | null;
              })}
            >
              {PII_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option === '' ? 'Not personal data' : option.replace('_', ' ')}
                </option>
              ))}
            </Select>
          </Field>
        )}

        <Field
          label="Width"
          hint="Narrower questions sit side by side on one row. They stack on small screens."
        >
          <Select
            value={element.layout?.width ?? 'full'}
            onChange={(e) => edit('Change width', (el) => {
              const width = e.target.value;

              // Cleared rather than stored as "full": the default is the absence of a
              // layout hint, and writing one in would make every question carry a setting
              // that says nothing.
              if (width === 'full') {
                if (el.layout) el.layout.width = undefined;
              } else {
                el.layout = { ...el.layout, width: width as 'half' | 'third' };
              }
            })}
          >
            <option value="full">Full width</option>
            <option value="half">Half — two per row</option>
            <option value="third">Third — three per row</option>
          </Select>
        </Field>

        {meta?.hasChoices && <ChoiceEditor element={element} edit={edit} />}

        {meta?.hasRows && <MatrixRowEditor element={element} edit={edit} />}
        {meta?.hasColumns && <MatrixColumnEditor element={element} edit={edit} />}

        {(element.type === 'rating' || element.type === 'slider') && (
          <div className="grid grid-cols-3 gap-2">
            <Field label="Min">
              <Input type="number" value={element.min ?? 0}
                     onChange={(e) => edit('Set min', (el) => { el.min = Number(e.target.value); })} />
            </Field>
            <Field label="Max">
              <Input type="number" value={element.max ?? 5}
                     onChange={(e) => edit('Set max', (el) => { el.max = Number(e.target.value); })} />
            </Field>
            {element.type === 'slider' && (
              <Field label="Step">
                <Input type="number" value={element.step ?? 1}
                       onChange={(e) => edit('Set step', (el) => { el.step = Number(e.target.value); })} />
              </Field>
            )}
          </div>
        )}

        {element.type === 'nps' && (
          <div className="grid grid-cols-2 gap-2">
            <Field label="Low label">
              <Input value={element.labels?.low ?? ''}
                     onChange={(e) => edit('Set NPS label', (el) => {
                       el.labels = { ...el.labels, low: e.target.value };
                     })} />
            </Field>
            <Field label="High label">
              <Input value={element.labels?.high ?? ''}
                     onChange={(e) => edit('Set NPS label', (el) => {
                       el.labels = { ...el.labels, high: e.target.value };
                     })} />
            </Field>
          </div>
        )}

        {element.type === 'calculated' && (
          <Field
            label="Expression"
            hint="Compiled to an AST at publish. No eval, so it runs under a strict CSP."
          >
            <Input
              value={element.expression ?? ''}
              onChange={(e) => edit('Set expression', (el) => { el.expression = e.target.value; })}
              placeholder="qty * unit_price"
              className="font-mono text-xs"
            />
          </Field>
        )}

        <ValidationEditor element={element} edit={edit} />
      </div>
    </div>
  );
}

/**
 * Renaming a ref is a breaking change: it appears in exports, webhooks, prefill and any
 * partner's integration code. The UI says so rather than letting it look cosmetic.
 */
function RefEditor({ element }: { element: FormElement }) {
  const definition = useBuilder((s) => s.definition);
  const apply = useBuilder((s) => s.apply);
  const select = useBuilder((s) => s.select);

  return (
    <Field
      label="Reference"
      hint="Used by exports, webhooks and prefill. Changing it breaks existing integrations."
    >
      <Input
        value={element.ref}
        className="font-mono text-xs"
        onChange={(e) => {
          if (!definition) return;
          const raw = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_');
          if (!raw || raw === element.ref) return;

          const next = uniqueRef(definition, raw);
          const previous = element.ref;

          apply('Rename reference', (draft) => {
            for (const page of draft.pages) {
              const target = page.elements.find((el) => el.ref === previous);
              if (target) { target.ref = next; break; }
            }

            // Keep logic and scoring pointing at the renamed question rather than
            // silently orphaning them into a publish-time error.
            for (const rule of draft.logic) {
              for (const action of rule.actions) {
                if (action.target?.question === previous) action.target.question = next;
              }
              retarget(rule.when, previous, next);
            }

            if (draft.scoring?.questions[previous]) {
              draft.scoring.questions[next] = draft.scoring.questions[previous]!;
              delete draft.scoring.questions[previous];
            }
          });

          select(next);
        }}
      />
    </Field>
  );
}

function retarget(
  condition: { left?: { ref?: string } | null; conditions?: unknown[] | null } | null | undefined,
  from: string, to: string,
) {
  if (!condition) return;
  if (condition.left?.ref === from) condition.left.ref = to;
  for (const child of condition.conditions ?? []) {
    retarget(child as Parameters<typeof retarget>[0], from, to);
  }
}

/**
 * The rows down the side of a matrix — the things being rated.
 *
 * Rows carry a `ref` rather than a `value`, because each one is answered separately and
 * that ref is what the answer is stored under. Renaming it after responses exist would
 * orphan them, which is why it is editable but shown as the identifier it is.
 */
function MatrixRowEditor({
  element, edit,
}: { element: FormElement; edit: (label: string, mutate: (el: FormElement) => void) => void }) {
  const rows = element.rows ?? [];

  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <span className="text-xs font-semibold">Rows</span>
        <span className="text-[11px] text-[var(--ff-text-muted)]">
          {rows.length} row{rows.length === 1 ? '' : 's'}
        </span>
      </div>

      <SortableRows
        count={rows.length}
        onMove={(from, to) => edit('Reorder rows', (el) => {
          if (!el.rows) return;
          el.rows.splice(to, 0, ...el.rows.splice(from, 1));
        })}
      >
        {rows.map((row, index) => (
          <SortableRow key={index} index={index} label={`Reorder row ${index + 1}`}>
            <Input
              value={row.label}
              aria-label={`Row ${index + 1} label`}
              onChange={(e) => {
                const label = e.target.value;
                const tracking = row.ref === slug(row.label) || !row.ref;
                edit('Edit row', (el) => {
                  const target = el.rows?.[index];
                  if (!target) return;
                  target.label = label;
                  if (tracking) target.ref = slug(label);
                });
              }}
            />
            <Input
              value={row.ref}
              aria-label={`Row ${index + 1} reference`}
              className="w-28 font-mono text-xs"
              onChange={(e) => {
                const ref = slug(e.target.value);
                edit('Edit row reference', (el) => {
                  const target = el.rows?.[index];
                  if (target) target.ref = ref;
                });
              }}
            />
            <IconButton
              label={`Remove row ${index + 1}`}
              disabled={rows.length <= 1}
              onClick={() => edit('Remove row', (el) => { el.rows?.splice(index, 1); })}
            >
              ×
            </IconButton>
          </SortableRow>
        ))}
      </SortableRows>

      <Button
        variant="ghost"
        className="mt-2 w-full"
        onClick={() => edit('Add row', (el) => {
          const next = (el.rows?.length ?? 0) + 1;
          el.rows = [...el.rows ?? [], { ref: `row_${next}`, label: `Row ${next}` }];
        })}
      >
        + Add row
      </Button>
    </div>
  );
}

/**
 * The columns across the top — the shared scale every row is answered against.
 *
 * Publish refuses a matrix with none, and until now there was no way to add one: matrices
 * were declared as choice questions, so the builder wrote the options into `choices`, which
 * the runtime does not render for a matrix. Same shape as a choice, different home.
 */
function MatrixColumnEditor({
  element, edit,
}: { element: FormElement; edit: (label: string, mutate: (el: FormElement) => void) => void }) {
  const columns = element.columns ?? [];

  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <span className="text-xs font-semibold">Columns</span>
        <span className="text-[11px] text-[var(--ff-text-muted)]">
          {columns.length} column{columns.length === 1 ? '' : 's'}
        </span>
      </div>

      {columns.length === 0 && (
        <p className="mb-2 text-xs text-[var(--ff-danger)]">
          A matrix needs at least one column before it can be published.
        </p>
      )}

      <SortableRows
        count={columns.length}
        onMove={(from, to) => edit('Reorder columns', (el) => {
          if (!el.columns) return;
          el.columns.splice(to, 0, ...el.columns.splice(from, 1));
        })}
      >
        {columns.map((column, index) => (
          <SortableRow key={index} index={index} label={`Reorder column ${index + 1}`}>
            <Input
              value={column.label}
              aria-label={`Column ${index + 1} label`}
              onChange={(e) => {
                const label = e.target.value;
                const tracking = column.value === slug(column.label) || !column.value;
                edit('Edit column', (el) => {
                  const target = el.columns?.[index];
                  if (!target) return;
                  target.label = label;
                  if (tracking) target.value = slug(label);
                });
              }}
            />
            <Input
              value={column.value}
              aria-label={`Column ${index + 1} stored value`}
              className="w-28 font-mono text-xs"
              onChange={(e) => {
                const value = slug(e.target.value);
                edit('Edit column value', (el) => {
                  const target = el.columns?.[index];
                  if (target) target.value = value;
                });
              }}
            />
            <IconButton
              label={`Remove column ${index + 1}`}
              onClick={() => edit('Remove column', (el) => { el.columns?.splice(index, 1); })}
            >
              ×
            </IconButton>
          </SortableRow>
        ))}
      </SortableRows>

      <Button
        variant="ghost"
        className="mt-2 w-full"
        onClick={() => edit('Add column', (el) => {
          const next = (el.columns?.length ?? 0) + 1;
          el.columns = [...el.columns ?? [], { value: `column_${next}`, label: `Column ${next}` }];
        })}
      >
        + Add column
      </Button>
    </div>
  );
}

/**
 * The per-choice switches, under the option they belong to.
 *
 * Split out so a choice row can hand them to the sortable row's footer: they describe this
 * one option, so they have to travel with it when it is dragged somewhere else.
 */
function ChoiceFlags({ choice, isMulti, onChange }: {
  choice: Choice;
  isMulti: boolean;
  onChange: (patch: Partial<Choice>) => void;
}) {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1">
      <label className="flex items-center gap-1.5 text-[11px]">
        <input
          type="checkbox"
          className="h-3.5 w-3.5 accent-[var(--ff-primary)]"
          checked={choice.allowText ?? false}
          onChange={(e) => onChange({ allowText: e.target.checked })}
        />
        Let them type their own answer
      </label>

      {/* Only offered on a multiple-choice question. "Clears the others" has no meaning
          where only one answer is possible, and setting it there is refused at publish
          with CHOICE_EXCLUSIVE_UNSUPPORTED. */}
      {isMulti && (
        <label className="flex items-center gap-1.5 text-[11px]">
          <input
            type="checkbox"
            className="h-3.5 w-3.5 accent-[var(--ff-primary)]"
            checked={choice.exclusive ?? false}
            onChange={(e) => onChange({ exclusive: e.target.checked })}
          />
          Clears the others (&ldquo;None of these&rdquo;)
        </label>
      )}
    </div>
  );
}

function ChoiceEditor({
  element, edit,
}: { element: FormElement; edit: (label: string, mutate: (el: FormElement) => void) => void }) {
  const choices = element.choices ?? [];

  const setChoice = (index: number, patch: Partial<Choice>) =>
    edit('Edit choice', (el) => {
      if (el.choices?.[index]) Object.assign(el.choices[index], patch);
    });

  const isMulti = element.type === 'multi_select';

  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <span className="text-xs font-semibold">Choices</span>
        <span className="text-[11px] text-[var(--ff-text-muted)]">
          {choices.length} option{choices.length === 1 ? '' : 's'}
        </span>
      </div>

      <div className="mb-2">
        <Field label="Order shown to respondents">
          <Select
            value={element.choicesOrder ?? 'asis'}
            onChange={(e) => edit('Change choice order', (el) => {
              el.choicesOrder = e.target.value === 'asis' ? null : e.target.value;
            })}
          >
            <option value="asis">As listed here</option>
            <option value="random">Random</option>
            <option value="asc">A to Z</option>
            <option value="desc">Z to A</option>
          </Select>
        </Field>
        {element.choicesOrder === 'random' && (
          <p className="mt-1 text-[11px] text-[var(--ff-text-muted)]">
            Each respondent gets their own order, and keeps it if they resume.
          </p>
        )}
      </div>

      <SortableRows
        count={choices.length}
        onMove={(from, to) => edit('Reorder choices', (el) => {
          if (!el.choices) return;
          el.choices.splice(to, 0, ...el.choices.splice(from, 1));
        })}
      >
        {choices.map((choice, index) => (
          <SortableRow
            key={index}
            index={index}
            label={`Reorder choice ${index + 1}`}
            footer={<ChoiceFlags choice={choice} isMulti={isMulti} onChange={(patch) => setChoice(index, patch)} />}
          >
            <Input
              value={choice.label}
              aria-label={`Choice ${index + 1} label`}
              onChange={(e) => {
                const label = e.target.value;
                // The stable value follows the label only while the author has not
                // customised it; once a response exists the value must not drift.
                const autoValue = choice.value === slug(choice.label) || !choice.value;
                setChoice(index, autoValue ? { label, value: slug(label) } : { label });
              }}
            />
            <Input
              value={choice.value}
              aria-label={`Choice ${index + 1} stored value`}
              onChange={(e) => setChoice(index, { value: slug(e.target.value) })}
              className="w-28 font-mono text-xs"
            />
            <IconButton
              label={`Remove choice ${index + 1}`}
              disabled={choices.length <= 1}
              onClick={() => edit('Remove choice', (el) => { el.choices?.splice(index, 1); })}
            >
              ×
            </IconButton>
          </SortableRow>
        ))}
      </SortableRows>

      <Button
        variant="ghost"
        className="mt-2 w-full"
        onClick={() => edit('Add choice', (el) => {
          const n = (el.choices?.length ?? 0) + 1;
          el.choices = [...(el.choices ?? []), { value: `option_${n}`, label: `Option ${n}` }];
        })}
      >
        + Add choice
      </Button>
    </div>
  );
}

function ValidationEditor({
  element, edit,
}: { element: FormElement; edit: (label: string, mutate: (el: FormElement) => void) => void }) {
  const rules = element.validation ?? [];
  const numeric = ['number', 'currency', 'rating', 'slider', 'nps'].includes(element.type ?? '');
  const available = numeric ? ['min', 'max'] : ['minLength', 'maxLength', 'regex'];

  return (
    <div>
      <span className="mb-1 block text-xs font-semibold">Validation</span>

      {rules.length === 0 && (
        <p className="mb-2 text-xs text-[var(--ff-text-muted)]">
          No rules. Every rule you add is enforced on the server too, not just in the browser.
        </p>
      )}

      <ul className="space-y-1.5">
        {rules.map((rule, index) => (
          <li key={index} className="flex items-center gap-1.5">
            <Select
              value={rule.rule}
              aria-label={`Rule ${index + 1} type`}
              onChange={(e) => edit('Change rule', (el) => {
                if (el.validation?.[index]) el.validation[index].rule = e.target.value;
              })}
              className="w-32"
            >
              {available.map((r) => <option key={r} value={r}>{r}</option>)}
            </Select>
            <Input
              value={String(rule.value ?? '')}
              aria-label={`Rule ${index + 1} value`}
              onChange={(e) => edit('Edit rule value', (el) => {
                if (!el.validation?.[index]) return;
                const raw = e.target.value;
                el.validation[index].value = /^\d+$/.test(raw) ? Number(raw) : raw;
              })}
            />
            <IconButton
              label={`Remove rule ${index + 1}`}
              onClick={() => edit('Remove rule', (el) => { el.validation?.splice(index, 1); })}
            >
              ×
            </IconButton>
          </li>
        ))}
      </ul>

      <Button
        variant="ghost"
        className="mt-2 w-full"
        onClick={() => edit('Add rule', (el) => {
          el.validation = [...(el.validation ?? []), { rule: available[0]!, value: 1 }];
        })}
      >
        + Add rule
      </Button>
    </div>
  );
}

const slug = (value: string) =>
  value.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 60) || 'option';

/**
 * Settings for a group, and for the repeat that makes it "add another".
 *
 * The maximum is not optional: the server requires it because it is what fixes the width of
 * the CSV export, so a form with an unbounded repeat cannot be published. Offering it as a
 * blank field would let somebody build a group that fails at the last step.
 */
function GroupProperties({
  element, edit,
}: { element: FormElement; edit: (label: string, mutate: (el: FormElement) => void) => void }) {
  const repeat = element.repeat ?? null;

  return (
    <div className="flex h-full flex-col">
      <header className="border-b border-[var(--ff-border)] px-4 py-3">
        <h2 className="text-sm font-semibold">Group</h2>
        <p className="text-xs text-[var(--ff-text-muted)]">
          {repeat ? 'Repeats for each entry' : 'A layout group'}
        </p>
      </header>

      <div className="min-h-0 flex-1 space-y-4 overflow-auto p-4">
        <Field label="Title">
          <Input
            value={element.title ?? ''}
            onChange={(e) => edit('Edit group title', (el) => { el.title = e.target.value; })}
          />
        </Field>

        <Field label="Description" hint="Optional text shown above the entries.">
          <Input
            value={element.description ?? ''}
            onChange={(e) => edit('Edit group description', (el) => {
              el.description = e.target.value || null;
            })}
          />
        </Field>

        {/* Only offered while the group does not repeat. A repeating group already has a
            shape — one block per entry — and columns would be arguing with it, which is
            also why the runtime ignores them there. */}
        {repeat === null && (
          <Field
            label="Columns"
            hint="How many questions sit side by side inside this block. They stack on small screens."
          >
            <Select
              value={String(element.layout?.columns ?? 1)}
              onChange={(e) => edit('Change columns', (el) => {
                const columns = Number(e.target.value);
                if (columns <= 1) {
                  if (el.layout) el.layout.columns = undefined;
                } else {
                  el.layout = { ...el.layout, columns };
                }
              })}
            >
              <option value="1">1 — stacked</option>
              <option value="2">2 columns</option>
              <option value="3">3 columns</option>
              <option value="4">4 columns</option>
            </Select>
          </Field>
        )}

        <Checkbox
          label="Let respondents add more than one"
          description="Turns the group into 'add another' — each entry is a separate set of answers."
          checked={repeat !== null}
          onChange={(on) => edit('Toggle repeating', (el) => {
            el.repeat = on
              ? { min: 0, max: 5, itemLabel: 'Entry {n}', addLabel: 'Add another' }
              : null;
          })}
        />

        {repeat && (
          <>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Fewest entries">
                <Input
                  type="number" min={0} max={repeat.max}
                  value={repeat.min}
                  onChange={(e) => edit('Change minimum entries', (el) => {
                    if (el.repeat) el.repeat.min = Math.max(0, Number(e.target.value) || 0);
                  })}
                />
              </Field>
              <Field label="Most entries" hint="Required.">
                <Input
                  type="number" min={1} max={50}
                  value={repeat.max}
                  onChange={(e) => edit('Change maximum entries', (el) => {
                    if (el.repeat) {
                      el.repeat.max = Math.min(50, Math.max(1, Number(e.target.value) || 1));
                    }
                  })}
                />
              </Field>
            </div>

            <Field
              label="Heading for each entry"
              hint="{n} becomes the entry's number. Without it the entries are hard to tell apart."
            >
              <Input
                value={repeat.itemLabel ?? ''}
                onChange={(e) => edit('Change entry heading', (el) => {
                  if (el.repeat) el.repeat.itemLabel = e.target.value || null;
                })}
              />
            </Field>

            <Field label="Add button">
              <Input
                value={repeat.addLabel ?? ''}
                placeholder="Add another"
                onChange={(e) => edit('Change add label', (el) => {
                  if (el.repeat) el.repeat.addLabel = e.target.value || null;
                })}
              />
            </Field>

            <p className="text-xs text-[var(--ff-text-muted)]">
              Each entry produces its own columns in the CSV export — {repeat.max} sets of
              them, whether or not anyone fills them all in. Logic inside a repeating group
              is not supported yet and will be refused when you publish.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
